import * as React from "react";
import { Dispatch, SetStateAction } from "react";

export interface DictationRefs {
  socketRef: React.MutableRefObject<WebSocket | null>;
  mediaRecorderRef: React.MutableRefObject<MediaRecorder | null>;
  audioStreamRef: React.MutableRefObject<MediaStream | null>;
  audioContextRef: React.MutableRefObject<AudioContext | null>;
  analyserRef: React.MutableRefObject<AnalyserNode | null>;
  audioProcessorRef: React.MutableRefObject<ScriptProcessorNode | null>;
  silenceTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  checkRecorderDataRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>;
  isRecordingRef: React.MutableRefObject<boolean>;
  isMicroLoadingRef: React.MutableRefObject<boolean>;
  isSpeakingRef: React.MutableRefObject<boolean>;
}

export interface DictationSetters {
  setIsRecording: Dispatch<SetStateAction<boolean>>;
  setIsMicroLoading: Dispatch<SetStateAction<boolean>>;
  setIsConverting: Dispatch<SetStateAction<boolean>>;
  setTranscript: Dispatch<SetStateAction<string>>;
  setIsSpeaking: Dispatch<SetStateAction<boolean>>;
}

export function cleanupAudioRefs(refs: DictationRefs): void {
  if (refs.socketRef.current) {
    refs.socketRef.current.close();
    // Null immediately so a re-entrant call (e.g. onclose firing after stopRecording
    // already called cleanup) does not attempt to close an already-closed socket.
    refs.socketRef.current = null;
  }
  if (refs.mediaRecorderRef.current && refs.mediaRecorderRef.current.state === "recording") {
    refs.mediaRecorderRef.current.stop();
  }
  if (refs.audioProcessorRef.current) {
    refs.audioProcessorRef.current.disconnect();
    refs.audioProcessorRef.current = null;
  }
  if (refs.analyserRef.current) {
    refs.analyserRef.current.disconnect();
    refs.analyserRef.current = null;
  }
  refs.audioStreamRef.current?.getTracks().forEach((track) => track.stop());
  refs.audioStreamRef.current = null;
  if (refs.audioContextRef.current && refs.audioContextRef.current.state !== "closed") {
    // Void the promise — AudioContext.close() returns a Promise but there is nothing
    // to await here; errors are non-actionable and must not become unhandled rejections.
    refs.audioContextRef.current.close().catch((err: unknown) => {
      console.warn("[DictationDebug] AudioContext close error (non-fatal):", err);
    });
    refs.audioContextRef.current = null;
  }
  if (refs.checkRecorderDataRef.current) {
    clearInterval(refs.checkRecorderDataRef.current);
    refs.checkRecorderDataRef.current = null;
  }
  if (refs.silenceTimerRef.current) {
    clearTimeout(refs.silenceTimerRef.current);
    refs.silenceTimerRef.current = null;
  }
}

export function resetAllStates(refs: DictationRefs, setters: DictationSetters): void {
  setters.setIsRecording(false);
  refs.isRecordingRef.current = false;
  setters.setIsMicroLoading(false);
  refs.isMicroLoadingRef.current = false;
  setters.setIsConverting(false);
  setters.setIsSpeaking(false);
  // Keep ref in sync with state so silence-detection guards read the correct value
  refs.isSpeakingRef.current = false;
}
